import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";

import { Toast } from "@/components/ui/Toast";
import { WorkflowLibrary } from "@/components/workflows/WorkflowLibrary";
import { useSpecs } from "@/hooks/useSpecs";
import { useThreads } from "@/hooks/useThreads";
import { buildSpecSlug } from "@domain/praxis-specs";

export const Route = createFileRoute("/_authenticated/workflows/")({
  component: WorkflowsLibraryRoute,
});

/**
 * The workflows library route — thin by design: Convex (`useSpecs`,
 * `useThreads`) and navigation live here; the grid + cards are pure
 * (`WorkflowLibrary` / `WorkflowCard`). See workflows-dedicated-ui §3.2.
 *
 * "Describe a new job" creates a fresh thread STAMPED `metadata.source:
 * "workflows"` (hidden from the chat sidebar) and lands on the gestation
 * route `/workflows/new/$threadId`, whose chat renders the two
 * workflow-template starter bubbles (`WORKFLOW_CHAT_STARTERS`) in place of
 * a generic seed — clicking one prefills the composer, never auto-sends.
 * The gestation route redirects to `/workflows/$slug` once the spec
 * projection lands. (The route still honors a `?seed=` deep link.)
 */
function WorkflowsLibraryRoute() {
  const { specs, isLoading } = useSpecs();
  const { createThread } = useThreads();
  const navigate = useNavigate();
  const [createFailed, setCreateFailed] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  return (
    <>
      <WorkflowLibrary
        specs={specs}
        isLoading={isLoading}
        onOpen={(specId) => {
          const item = specs.find((s) => s.spec._id === specId);
          void navigate({
            to: "/workflows/$slug",
            params: {
              slug: item
                ? buildSpecSlug({ title: item.spec.title, specId })
                : specId,
            },
            search: { tab: "overview" },
          });
        }}
        onNewWorkflow={() => {
          void createThread({
            title: "New workflow",
            metadata: { source: "workflows" },
          })
            .then((threadId) => {
              void navigate({
                to: "/workflows/new/$threadId",
                params: { threadId: threadId as string },
                search: {},
              });
            })
            .catch(() => {
              setCreateFailed(true);
              clearTimeout(toastTimer.current);
              toastTimer.current = setTimeout(() => setCreateFailed(false), 4000);
            });
        }}
      />
      <Toast
        isVisible={createFailed}
        icon={<AlertTriangle className="h-4 w-4 text-home-terracotta" />}
        message="Couldn't start a new workflow — try again."
      />
    </>
  );
}
